import { useMemo } from 'react'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  type AutoPayPaymentMethodPaymentTypeEnum,
  type PayMethod,
  PayMethodTypeEnum,
  PayMethodStatusEnum,
} from '@b-stock/payments-methods-api-client'

import {
  canPayWithNetTerms,
  getSortedValidPayEntities,
  isDirectPaymentMethod,
  isPayMethodBlocked,
  isBuyerDenied,
} from '@/buyer-portal/components/features/order/checkout/helpers/utils'
import { isAchMethod } from '@/buyer-portal/components/features/order/checkout/PaymentMethods/utils'
import type { NetTermsPayMethod } from '@/buyer-portal/components/features/order/checkout/shared'
import { useListingPaymentRestrictions } from '@/buyer-portal/helpers/useListingPaymentRestrictions'
import { usePaymentRestrictions } from '@/buyer-portal/helpers/usePaymentRestrictions'
import savedAutoPayQuery from '@/buyer-portal/queries/savedAutoPayQuery'
import { FeatureFlags } from '@/helpers/featureFlags'

import usePaymentEntitiesQuery from './usePaymentEntitiesQuery'

type AutoPaymentMethodsResult = {
  paymentMethods: PayMethod[]
  defaultPaymentMethod: PayMethod | null
  savedPaymentType: AutoPayPaymentMethodPaymentTypeEnum | null
  /** Includes the account default on a first bid, before a listing preference exists. */
  isSavedAchBlocked: boolean
  isWireFallbackAvailableForSavedAch?: boolean
  isSavedAutoPayFetched: boolean
  isSavedAutoPayFetching: boolean
  isSavedMethodInvalid: boolean
  isLoading: boolean
  error: unknown
  refetch: () => Promise<unknown>
}

export const useAutoPaymentMethods = ({
  accountId,
  enabled,
  payableAmountInCents,
  payForId = '',
  payForType,
  listingIsCreditCardEnabled,
}: {
  accountId: string | null
  enabled: boolean
  /**
   * Optional: Amount to pay in cents. Used to determine if Net Terms has sufficient credit.
   * If not provided, defaults to Net Terms if available, otherwise first regular method.
   */
  payableAmountInCents?: number
  payForId?: string
  payForType?: 'listing' | 'contract'
  /**
   * Whether the listing has credit card payments enabled (`!!listing.isCreditCardEnabled`).
   * When false, credit card methods are excluded from the computed default.
   */
  listingIsCreditCardEnabled?: boolean
}): AutoPaymentMethodsResult => {
  const autoPayEnforced = useFlag(FeatureFlags.AUTOPAY_ENFORCED)
  const isBalanceCCFeatureEnabled = useFlag<boolean>(
    FeatureFlags.ENABLE_BALANCE_CREDIT_CARDS
  )
  const payRestrictions = useListingPaymentRestrictions(
    listingIsCreditCardEnabled
  )
  // Use account-only restrictions for copy so listing-level CC disablement
  // still falls back to the generic select-a-payment-method prompt.
  const accountRestrictions = usePaymentRestrictions()

  const paymentMethodsResult = usePaymentEntitiesQuery({ enabled })

  const {
    data: paymentMethods,
    isLoading: isPaymentMethodsLoading,
    error,
    refetch,
  } = paymentMethodsResult

  const {
    data: savedAutoPayData,
    isFetched: isSavedAutoPayFetched,
    isFetching: isSavedAutoPayFetching,
    isLoading: isSavedAutoPayLoading,
  } = savedAutoPayQuery.useWithOpts(
    [accountId ?? '', payForId, payForType ?? 'listing'],
    { enabled: enabled && !!accountId && !!payForId && !!payForType }
  )

  // FF autoPayEnforced: filter to reusable, but keep all net terms (including
  // canceled/failed) so their disabled state renders correctly — matches checkout
  const reusablePaymentMethods = useMemo(() => {
    if (!paymentMethods) return []
    if (!autoPayEnforced) return paymentMethods
    return paymentMethods.filter(
      (pm) =>
        pm.type === PayMethodTypeEnum.Getbalance ||
        pm.status === PayMethodStatusEnum.Reusable
    )
  }, [paymentMethods, autoPayEnforced])

  const savedPaymentMethod = useMemo(() => {
    if (!savedAutoPayData?.paymentMethodId) return null
    return (
      reusablePaymentMethods.find(
        (pm) => pm.id === savedAutoPayData.paymentMethodId
      ) ?? null
    )
    // oxlint-disable-next-line react/preserve-manual-memoization
  }, [reusablePaymentMethods, savedAutoPayData?.paymentMethodId])

  const isSavedAchBlocked =
    savedPaymentMethod !== null &&
    isAchMethod(savedPaymentMethod.type) &&
    accountRestrictions.isACHBlocked

  const isSavedMethodInvalid = useMemo(() => {
    if (
      !savedAutoPayData?.paymentMethodId ||
      reusablePaymentMethods.length === 0
    ) {
      return false
    }

    const savedMethod = reusablePaymentMethods.find(
      (pm) => pm.id === savedAutoPayData.paymentMethodId
    )

    if (!savedMethod) return false

    if (savedMethod.type === PayMethodTypeEnum.Getbalance) {
      return isBuyerDenied(savedMethod) || accountRestrictions.isNetTermsBlocked
    }

    // For saved digital methods, use account-level restrictions for the copy.
    return (
      autoPayEnforced && isPayMethodBlocked(savedMethod, accountRestrictions)
    )
    // oxlint-disable-next-line react/preserve-manual-memoization
  }, [
    autoPayEnforced,
    accountRestrictions,
    reusablePaymentMethods,
    savedAutoPayData?.paymentMethodId,
  ])

  const { defaultPaymentMethod, defaultAchBlocked } = useMemo(() => {
    // Resolve the normal default as well as the usable default. A saved bank on
    // the account does not imply a saved auto-pay record for this listing.
    const resolveDefault = (ignoreAchBlock: boolean): PayMethod | null => {
      const restrictions = {
        ...payRestrictions,
        isACHBlocked: !ignoreAchBlock && accountRestrictions.isACHBlocked,
      }
      const savedAchBlocked = !ignoreAchBlock && isSavedAchBlocked
      if (reusablePaymentMethods.length === 0) return null

      // allowCreditCards intentionally combines both the platform feature flag and listing
      // configuration: the default selection must be actionable, so cards are excluded when
      // the listing does not accept them — not only when the platform feature is off.
      const regularMethods = reusablePaymentMethods.filter(
        (pm) =>
          isDirectPaymentMethod(
            pm.type,
            isBalanceCCFeatureEnabled && !!listingIsCreditCardEnabled
          ) && !isPayMethodBlocked(pm, restrictions)
      )

      // FF autoPayEnforced: use checkout's getSortedValidPayEntities
      if (autoPayEnforced) {
        const sorted = getSortedValidPayEntities(
          reusablePaymentMethods,
          restrictions,
          {
            allowCreditCards:
              isBalanceCCFeatureEnabled && !!listingIsCreditCardEnabled,
          }
        )
        const sortedFallbackMethods = sorted.filter(
          (pm) => !isPayMethodBlocked(pm, restrictions)
        )

        if (savedAutoPayData?.paymentMethodId) {
          const savedMethod = savedPaymentMethod
          if (savedMethod) {
            if (!savedAchBlocked) {
              // Honor saved choice; return null if blocked so buyer re-confirms and backend record updates.
              if (savedMethod.type === PayMethodTypeEnum.Getbalance) {
                const isSavedNetTermsUnavailable =
                  isBuyerDenied(savedMethod) ||
                  accountRestrictions.isNetTermsBlocked

                return isSavedNetTermsUnavailable ? null : savedMethod
              }
              return isPayMethodBlocked(savedMethod, restrictions) ||
                !sorted.some((m) => m.id === savedMethod.id)
                ? null
                : savedMethod
            }
          }
          // Method deleted from account — fall through to sorted[0]
          // Saved ACH blocked by account — also fall through to the existing sorted fallback.
        }

        if (sortedFallbackMethods.length > 0)
          return sortedFallbackMethods[0] ?? null

        // Fallback: net terms if approved & can cover the amount
        const netTermsMethod = reusablePaymentMethods.find(
          (pm) => pm.type === PayMethodTypeEnum.Getbalance
        ) as NetTermsPayMethod | undefined
        if (
          !accountRestrictions.isNetTermsBlocked &&
          canPayWithNetTerms(payableAmountInCents ?? 0, netTermsMethod)
        ) {
          return netTermsMethod ?? null
        }

        return null
      }

      // Legacy (FF off): saved auto-pay method first, then net terms, then regular
      if (savedAutoPayData?.paymentMethodId) {
        const savedMethod = savedPaymentMethod
        if (savedMethod) {
          if (!savedAchBlocked) {
            if (
              isBuyerDenied(savedMethod) ||
              isPayMethodBlocked(savedMethod, restrictions)
            ) {
              return null
            }
            // net terms is excluded from regular methods & is checked via isBuyerDenied above.
            // saved method must be in regular methods to ensure the default is visible and actionable.
            if (
              savedMethod.type === PayMethodTypeEnum.Getbalance ||
              regularMethods.some((m) => m.id === savedMethod.id)
            ) {
              return savedMethod
            }
            // Not in visible set of payment methods, return null to force re-selection and sync the backend record.
            return null
          }
          // Saved ACH blocked by account — fall through to the existing fallback ordering.
        }
      }

      // Legacy (FF off): net terms before regular methods
      const netTermsMethod = reusablePaymentMethods.find(
        (pm) => pm.type === PayMethodTypeEnum.Getbalance
      ) as NetTermsPayMethod | undefined

      // Priority 2: net terms method, if approved & can cover payable amount
      // getbalance is typed as always-present, but a malformed payments-methods-svc
      // response can omit it at runtime.
      if (
        // oxlint-disable-next-line typescript-eslint/no-unnecessary-condition
        netTermsMethod?.getbalance?.qualification?.status === 'approved' &&
        !isBuyerDenied(netTermsMethod) &&
        !accountRestrictions.isNetTermsBlocked &&
        payableAmountInCents &&
        payableAmountInCents > 0
      ) {
        const availableCredit =
          netTermsMethod.details.creditInfo?.availableCreditInCents ?? 0
        if (availableCredit >= payableAmountInCents) {
          return netTermsMethod
        }
      }

      // Priority 3: fallback to default regular payment method: primary or first
      if (regularMethods.length > 0) {
        const primaryMethod = regularMethods.find((pm) => !!pm.primaryAt)
        return primaryMethod ?? regularMethods[0] ?? null
      }

      // no suitable default found
      return null
    }
    const normalDefault = resolveDefault(true)
    return {
      defaultPaymentMethod: resolveDefault(false),
      defaultAchBlocked:
        savedAutoPayData?.paymentType !== 'wire' &&
        accountRestrictions.isACHBlocked &&
        normalDefault !== null &&
        isAchMethod(normalDefault.type),
    }
  }, [
    reusablePaymentMethods,
    savedAutoPayData,
    payableAmountInCents,
    autoPayEnforced,
    payRestrictions,
    accountRestrictions,
    isSavedAchBlocked,
    listingIsCreditCardEnabled,
    isBalanceCCFeatureEnabled,
    savedPaymentMethod,
  ])

  return {
    /**
     * All available payment methods for the account
     * (FF on: filtered to reusable only; FF off: all methods)
     */
    paymentMethods: reusablePaymentMethods,
    /**
     * The computed default payment method (considers saved auto-pay, priority, credit)
     */
    defaultPaymentMethod: accountRestrictions.isAccountLoading
      ? null
      : defaultPaymentMethod,
    /**
     * The payment type stored for this listing/contract.
     * 'wire' means the buyer last confirmed with wire transfer.
     * 'digital' covers all non-wire methods (card, bank, ACH, net terms) including
     * pre-existing records that predate this field (treated as digital by default).
     * null means no auto-pay record exists yet for this listing/contract.
     */
    savedPaymentType: savedAutoPayData?.paymentType ?? null,
    isSavedAchBlocked: isSavedAchBlocked || defaultAchBlocked,
    isWireFallbackAvailableForSavedAch:
      (isSavedAchBlocked || defaultAchBlocked) &&
      accountRestrictions.isWireFallbackAvailable === true,
    isSavedAutoPayFetched,
    isSavedAutoPayFetching,
    isSavedMethodInvalid,
    refetch,
    isLoading:
      accountRestrictions.isAccountLoading === true ||
      isPaymentMethodsLoading ||
      isSavedAutoPayLoading,
    error,
  }
}
